import { cn } from "cn";
import type { ReactNode } from "react";
import { BwWarningCircleFill } from "@/components/ui/icons";

type AddressWarningProps = {
  className?: string;
};

/**
 * The panel both address warnings are drawn in. Private on purpose — the copy is the
 * knowledge worth keeping in one home, and the two exports below own it.
 */
function AddressWarningShell({
  title,
  children,
  className,
}: AddressWarningProps & { title: string; children: ReactNode }) {
  return (
    <div className={cn("flex gap-2 p-4 rounded-xl bg-bg-dark-min", className)}>
      <BwWarningCircleFill className="text-text-negative shrink-0" size={24} />
      <div className="text-left text-sm leading-5">
        <div className="font-medium text-text-dark-max">{title}</div>
        <div className="text-text-dark-high mt-1">{children}</div>
      </div>
    </div>
  );
}

/**
 * Address validation did not recognize the address. Not a dead end: the caller keeps the
 * submit button live so the user can insist the address is correct.
 */
export function UnverifiedAddressWarning({ className }: AddressWarningProps) {
  return (
    <AddressWarningShell
      title="Double check the address you've entered"
      className={className}
    >
      If you're confident that it's correct, you can proceed by clicking the
      button below.
    </AddressWarningShell>
  );
}

/**
 * The address resolves to a place that already has an active membership — a dead end, so
 * the caller disables submit and support is the only way forward.
 *
 * `onContactSupport` is a prop rather than a `useIntercom()` call inside: the member funnel
 * runs under `SupportChatIdentify` and can pass `show` directly, while the presale funnel is a
 * public route with no boot and has to boot an anonymous session first.
 */
export function PlaceAlreadyActiveWarning({
  className,
  onContactSupport,
}: AddressWarningProps & { onContactSupport: () => void }) {
  return (
    <AddressWarningShell title="Ooops, something is off." className={className}>
      Please check the details and try again or{" "}
      <span
        className="text-text-primary cursor-pointer hover:underline"
        onClick={onContactSupport}
      >
        contact us
      </span>{" "}
      for further assistance.
    </AddressWarningShell>
  );
}
